import { BankKitRequestRecipient } from '@interfaces/partners/requests/bank-kit-request.request';

/** Um destinatário na modal do Kit Banco, com a marcação de "vai receber". */
export interface BankKitRecipientOption extends BankKitRequestRecipient {
  selected: boolean;
}

/** Formato de e-mail aceito na modal (a validação final é do backend). */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** E-mail sem espaços nas pontas e em minúsculas, para comparar. */
export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** O texto parece um e-mail. */
export function isValidEmail(email: string | null | undefined): boolean {
  return EMAIL_PATTERN.test((email ?? '').trim());
}

/**
 * Lista inicial: os sugeridos pelo backend (contatos e usuários da empresa), sem repetir e-mail. Nenhum vem marcado:
 * quem pede escolhe para quem vai.
 */
export function buildRecipientOptions(
  suggested: BankKitRequestRecipient[] | null | undefined
): BankKitRecipientOption[] {
  const seen = new Set<string>();
  const options: BankKitRecipientOption[] = [];
  for (const recipient of suggested ?? []) {
    const key = normalizeEmail(recipient.email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({ ...recipient, email: recipient.email.trim(), selected: false });
  }
  return options;
}

/**
 * Acrescenta um e-mail digitado, já marcado. Se ele já está na lista, só marca. Devolve a lista nova e um erro quando
 * o texto não é e-mail.
 */
export function addManualRecipient(
  options: BankKitRecipientOption[],
  rawEmail: string
): { options: BankKitRecipientOption[]; error: string | null } {
  const email = (rawEmail ?? '').trim();
  if (!isValidEmail(email)) {
    return { options, error: 'Digite um e-mail válido, como nome@empresa.com.br.' };
  }

  const key = normalizeEmail(email);
  if (options.some(o => normalizeEmail(o.email) === key)) {
    return {
      options: options.map(o => (normalizeEmail(o.email) === key ? { ...o, selected: true } : o)),
      error: null,
    };
  }

  return {
    options: [...options, { name: null, email, source: 'manual', selected: true }],
    error: null,
  };
}

/** Rótulo da origem mostrado ao lado do e-mail. */
export function describeRecipientSource(source: BankKitRequestRecipient['source']): string {
  switch (source) {
    case 'contact':
      return 'Contato da empresa';
    case 'user':
      return 'Usuário da empresa';
    default:
      return 'Digitado agora';
  }
}
